// A document's processing runs, newest first: every step the pipeline took over
// the file, how each one ended and why (`document-ai/0022` §3). The other tabs
// show what extraction produced; this one shows how it got there.
//
// The project's Categories tab shows an import's runs with it too: the check,
// the brief and the families are rows of the same shape, run by the bundle
// job, with no hash.
import type { ProcessingStepRow } from "@gb/admin-contracts";
import {
  PROCESSING_STEP_LABELS,
  PROCESSING_STEP_OUTCOME_LABELS,
  ProcessingStepSchema,
  type ProcessingStepOutcome,
} from "@gb/contracts";
import { cn } from "@/lib/utils";

/**
 * The runs page's status colours, extended to the step outcomes. `unsupported`
 * is amber: a format with no reader is not a failure, but it is not a clean
 * read either. `partial` is amber for the same reason: some of its inputs were
 * not done.
 */
const OUTCOME_TONES: Record<ProcessingStepOutcome, string> = {
  ok: "text-ok",
  partial: "text-warn",
  skipped: "text-faint",
  unsupported: "text-warn",
  failed: "text-danger",
};

export type ProcessingRun = {
  runId: string;
  /** 1 for the document's oldest run, counting distinct run ids. */
  number: number;
  attempt: number;
  startedAt: string;
  /** The first hash any step recorded. Null until a fetch has read the bytes. */
  fileSha256: string | null;
  steps: ProcessingStepRow[];
};

/**
 * Group the rows, which arrive oldest first, into runs, newest first.
 *
 * A run is one `(runId, attempt)` pair, not one `runId`. The sweeper re-runs a
 * stuck job under the same id with the next attempt, and each attempt is its
 * own pass over the file. Both attempts keep the run's number, so "Run 2 ·
 * attempt 2" reads as the retry of "Run 2 · attempt 1".
 *
 * Within a run, steps are shown in the pipeline's order — the order
 * `ProcessingStepSchema` lists them in — not by time. `sniff` happens inside
 * the router before `parse`, but is recorded after it, because the format it
 * found only comes back on the parse's result.
 */
export function groupRuns(steps: readonly ProcessingStepRow[]): ProcessingRun[] {
  const numbers = new Map<string, number>();
  const runs = new Map<string, ProcessingRun>();
  for (const step of steps) {
    let number = numbers.get(step.runId);
    if (number === undefined) {
      number = numbers.size + 1;
      numbers.set(step.runId, number);
    }
    const key = `${step.runId}:${step.attempt}`;
    let run = runs.get(key);
    if (!run) {
      run = {
        runId: step.runId,
        number,
        attempt: step.attempt,
        startedAt: step.startedAt,
        fileSha256: null,
        steps: [],
      };
      runs.set(key, run);
    }
    run.fileSha256 ??= step.fileSha256;
    run.steps.push(step);
  }
  for (const run of runs.values()) {
    run.steps.sort((a, b) => PIPELINE_ORDER.indexOf(a.step) - PIPELINE_ORDER.indexOf(b.step));
  }
  return [...runs.values()].reverse();
}

const PIPELINE_ORDER: readonly string[] = ProcessingStepSchema.options;

function durationMs(step: ProcessingStepRow): number {
  return Date.parse(step.finishedAt) - Date.parse(step.startedAt);
}

export function ProcessingStepsPanel({ steps }: { steps: ProcessingStepRow[] | undefined }) {
  if (!steps) return null;
  if (steps.length === 0) {
    return <p className="text-[0.8rem] text-muted">No processing steps recorded yet.</p>;
  }
  return (
    <>
      {groupRuns(steps).map((run) => (
        <div
          key={`${run.runId}:${run.attempt}`}
          className="rounded-lg border border-border-inner bg-white p-4"
        >
          <div className="font-mono text-[0.7rem] text-faint">
            Run {run.number} · attempt {run.attempt} · {new Date(run.startedAt).toLocaleString()}
          </div>
          {run.fileSha256 && (
            <div className="mt-0.5 font-mono text-[0.68rem] break-all text-faint">
              sha256 {run.fileSha256}
            </div>
          )}
          <ol className="mt-2 divide-y divide-border-inner">
            {run.steps.map((s) => (
              <li key={s.id} className="py-1.5">
                <div className="flex items-baseline gap-3">
                  <div className="min-w-0 flex-1 text-[0.78rem] text-ink">
                    {PROCESSING_STEP_LABELS[s.step]}
                    <span className="ml-2 font-mono text-[0.68rem] text-faint">
                      {s.step} v{s.stepVersion}
                    </span>
                  </div>
                  <span className={cn("font-mono text-[0.72rem]", OUTCOME_TONES[s.outcome])}>
                    {PROCESSING_STEP_OUTCOME_LABELS[s.outcome]}
                  </span>
                  <span className="w-16 shrink-0 text-right font-mono text-[0.72rem] text-muted">
                    {durationMs(s)}ms
                  </span>
                </div>
                {s.reason && (
                  <p className="mt-0.5 text-[0.75rem] leading-relaxed text-ink-prose">{s.reason}</p>
                )}
                {s.detail && (
                  <pre className="mt-0.5 font-mono text-[0.68rem] break-all whitespace-pre-wrap text-muted">
                    {JSON.stringify(s.detail)}
                  </pre>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </>
  );
}
